import { FormikProps } from 'formik';
import { Col, Form, Row } from 'antd';
import { memo } from 'react';

import dayjs from 'dayjs';
import { MDatePicker, MInput, MSelect, MTextArea } from '@Components/Base';
import { Option } from '@Components/types/options';
import { prepareDatePickerValue } from '@Utils/transformDateTime';

import { PassportRequestFormValues } from '../type';

interface RequestDetailsSectionProps {
  formik: FormikProps<PassportRequestFormValues>;
  label: string;
  formApproval: boolean;
  passportTypeOptions: Option[];
}

const RequestDetailsSection: React.FC<RequestDetailsSectionProps> = ({
  formik,
  formApproval,
  passportTypeOptions,
}) => {
  const {
    purpose: purposeTouched,
    fromDate: fromDateTouched,
    returnDate: returnDateTouched,
    totDays: totDaysTouched,
    remarks: remarksTouched,
  } = formik.touched;
  const {
    purpose: purposeError,
    fromDate: fromDateError,
    returnDate: returnDateError,
    totDays: totDaysError,
    remarks: remarksError,
  } = formik.errors;

  return (
    <Row>
      <Col span={24}>
        <Form.Item
          label="Purpose"
          required={!formApproval}
          validateStatus={purposeError && purposeTouched ? 'error' : ''}
          help={purposeError && purposeTouched ? purposeError : ''}
        >
          <MSelect
            labeloption="Purpose"
            options={passportTypeOptions}
            onChange={(selectValue) =>
              formik.setFieldValue('purpose', selectValue)
            }
            onBlur={() => {
              formik.setFieldTouched('purpose', true);
            }}
            value={formik.values.purpose}
            disabled={formApproval}
            placeholder="Select Purpose"
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Remarks / Reason"
          validateStatus={remarksError && remarksTouched ? 'error' : ''}
          help={remarksError && remarksTouched ? remarksError : ''}
        >
          <MTextArea
            name="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            onBlur={() => {
              formik.setFieldTouched('remarks', true);
            }}
            rows={4}
            showCount
            maxLength={100}
            disabled={formApproval}
            placeholder="Leave a comment"
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          label="From Date"
          required={!formApproval}
          validateStatus={fromDateError && fromDateTouched ? 'error' : ''}
          help={fromDateError && fromDateTouched ? fromDateError : ''}
        >
          <MDatePicker
            className="leave-deatails-datepicker"
            name="fromDate"
            value={
              formik.values.fromDate && dayjs(formik.values.fromDate).tz('UTC')
            }
            format="DD/MM/YYYY"
            onChange={(value) =>
              formik.setFieldValue(
                'fromDate',
                prepareDatePickerValue(value, true)
              )
            }
            onBlur={() => {
              formik.setFieldTouched('fromDate', true);
            }}
            disabledDate={(current) => {
              // Disable past days
              return current && current < dayjs().startOf('day');
            }}
            disabled={formApproval}
            placeholder="Select Date"
          />
        </Form.Item>
      </Col>
      <Col span={11} push={1}>
        <Form.Item
          label="Return Date"
          required={!formApproval}
          validateStatus={returnDateError && returnDateTouched ? 'error' : ''}
          help={returnDateError && returnDateTouched ? returnDateError : ''}
        >
          <MDatePicker
            className="leave-deatails-datepicker"
            name="returnDate"
            value={
              formik.values.returnDate &&
              dayjs(formik.values.returnDate).tz('UTC')
            }
            format="DD/MM/YYYY"
            onChange={(value) => {
              formik.setFieldValue(
                'returnDate',
                prepareDatePickerValue(value, true)
              );
            }}
            onBlur={() => {
              formik.setFieldTouched('returnDate', true);
            }}
            disabledDate={(current) => {
              if (formik.values.fromDate) {
                // disable days less than chosen fromDate
                return (
                  current &&
                  current < dayjs(formik.values.fromDate).startOf('day')
                );
              }
              // Disable past days
              return current && current < dayjs().startOf('day');
            }}
            disabled={formApproval}
            placeholder="Select Date"
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Total No. of days"
          required={!formApproval}
          validateStatus={totDaysError && totDaysTouched ? 'error' : ''}
          help={totDaysError && totDaysTouched ? totDaysError : ''}
        >
          <MInput
            className="leave-details-input"
            name="totDays"
            value={formik.values.totDays}
            disabled
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export default memo(RequestDetailsSection);
